import React from 'react';
import { Link } from 'react-router-dom';
import { Waves, Sparkles, UtensilsCrossed, Umbrella, Dumbbell, Bell, Car, Map } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { langPath } from '../utils/siteNav';

const iconMap = {
  Waves: Waves,
  Sparkles: Sparkles,
  UtensilsCrossed: UtensilsCrossed,
  Umbrella: Umbrella,
  Dumbbell: Dumbbell,
  ConciergeBell: Bell,
  Car: Car,
  Map: Map
};

const Amenities = () => {
  const { t, language } = useLanguage();

  const amenities = [
    {
      id: 1,
      name: t('amenities.rooftopPool'),
      description: t('amenities.rooftopPoolDesc'),
      icon: 'Waves'
    },
    {
      id: 2,
      name: t('amenities.restaurantBar'),
      description: t('amenities.restaurantBarDesc'),
      icon: 'UtensilsCrossed'
    },
    {
      id: 3,
      name: t('amenities.freeParking'),
      description: t('amenities.freeParkingDesc'),
      icon: 'Car'
    },
    {
      id: 4,
      name: t('amenities.sunTerrace'),
      description: t('amenities.sunTerraceDesc'),
      icon: 'Umbrella'
    },
    {
      id: 5,
      name: t('amenities.airportShuttle'),
      description: t('amenities.airportShuttleDesc'),
      icon: 'Map'
    },
    {
      id: 6,
      name: t('amenities.frontDesk'),
      description: t('amenities.frontDeskDesc'),
      icon: 'ConciergeBell'
    }
  ];

  return (
    <section id="amenities" className="py-24 bg-slate-800 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-amber-400 font-medium tracking-widest text-sm uppercase">{t('amenities.subtitle')}</span>
          <h2 className="text-4xl md:text-5xl font-serif text-white mt-3 mb-4">{t('amenities.title')}</h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            {t('amenities.description')}
          </p>
        </div>

        {/* Amenities Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {amenities.map((amenity, index) => {
            const Icon = iconMap[amenity.icon] || Waves;
            return (
              <div
                key={amenity.id}
                className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 hover:border-amber-500/50 hover:-translate-y-1"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-amber-600 to-amber-700 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-amber-500/25 group-hover:scale-110 transition-transform duration-300">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{amenity.name}</h3>
                <p className="text-slate-400 leading-relaxed">{amenity.description}</p>
              </div>
            );
          })}
        </div>

        {/* Feature Highlight */}
        <div className="mt-16 bg-gradient-to-r from-amber-600 to-amber-500 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl md:text-3xl font-serif text-white mb-2">{t('amenities.experienceLuxury')}</h3>
            <p className="text-amber-100">
              {t('amenities.experienceLuxuryDesc')}
            </p>
          </div>
          <Link
            to={langPath(language, 'contact')}
            className="bg-white text-amber-700 px-8 py-4 rounded-full font-semibold hover:bg-amber-50 transition-colors duration-300 shadow-lg whitespace-nowrap"
            data-testid="amenities-contact-link"
          >
            {t('amenities.contactConcierge')}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Amenities;
